import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiUrls from '../../api/apiUrls';
import fetchWithAuth from '../../api/fetchWithAuth';
import { useLang } from "../../contexts/LanguageContext";
import Loader from '../Loader';
import { Alert, Button, Collapse, Form } from 'react-bootstrap';
import './BookInfo.css';


const BookInfo = () => {
  
  /* Inciar constante de traducción */
  const { t } = useLang();

  /* Colapso del resumen  */
  const [open, setOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('book_id');

  const [bookData, setBookData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("queued");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const normalizedBookId = bookId;

  const statusOptions = useMemo(
    () => [
      { value: "queued", label: t("LIST_STATUS_QUEUED"), icon:"🕓" },
      { value: "in_progress", label: t("LIST_STATUS_IN_PROGRESS"), icon:"📖"  },
      { value: "finished", label: t("LIST_STATUS_FINISHED"), icon:"🏁"  },
    ],
    [t]
  );

  useEffect(() => {
    if (!normalizedBookId) return;

    const loadBookInfo = async () => {
      try {
        const response = await fetchWithAuth(`${apiUrls.bookInfoApi}/${encodeURIComponent(normalizedBookId)}`);
        const result = await response.json();

        if (result.data) {
          setBookData(result.data);
        } else {
          setError(result.error || 'Error desconocido');
        }
      } catch (err) {
        setError('Error al obtener datos del libro');
        console.error(err);
      }
    };

    loadBookInfo();
  }, [normalizedBookId]);

  const handleAddToList = async () => {
    if (!normalizedBookId) {
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const payload = { status };

      const response = await fetchWithAuth(apiUrls.readingListItem(normalizedBookId), {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const rawMessage = await response.text();
        throw new Error(rawMessage || `HTTP ${response.status}`);
      }

      let parsed = null;
      try {
        parsed = await response.json();
      } catch {
        console.error("Error.");
      }

      setFeedback({
        type: "success",
        message:
          parsed?.message && typeof parsed.message === "string"
            ? parsed.message
            : t("BOOKINFO_ADD_SUCCESS"),
      });

      window.dispatchEvent(
        new CustomEvent("reading-list-updated", {
          detail: { bookId: normalizedBookId, status },
        })
      );
    } catch (err) {
      console.error("Error updating reading list:", err);
      setFeedback({ type: "error", message: t("BOOKINFO_ADD_ERROR") });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Fondo de la lista de capítulos del libro */
  const backgroundUrl = bookData ? apiUrls.apiUrlBookCover(bookId) : ""; 

  if (error) return <p>ERROR: {error}</p>;
  if (!bookData) return <Loader visible="true"/>;


  return (
    
    <div className="book_info_container">
      <div className="row">
          <div className="col-md" id="book_info">

              <div id="book_info_text">
                <h1>{bookData.book_title}</h1>
                <p>{bookData.book_author} ({bookData.book_print_date}) - {bookData.language_name}</p>
              </div>
              
              <div id="book_resume" className="d-none d-md-block">{bookData.book_resume}</div>

              {/* Show the button only in small screens */}
              <div className="d-block d-md-none">
                <Button
                  onClick={() => setOpen(!open)}
                  aria-controls="collapse-content"
                  aria-expanded={open}
                >
                  {open ? t("SHOW_BOOK_RESUME") : t("HIDE_BOOK_RESUME")}
                </Button>
              </div>
              {/*  Show the button only in small screens  */}
              <div className="d-block d-md-none">
                <Collapse in={open}>
                  <div id="collapse-content" className="mt-3">
                  {bookData.book_resume}
                  </div>
                </Collapse>
              </div>

              <div className="mt-3">
                <Form className="">
                  <Form.Group controlId="bookStatus">
                    <Form.Label className="small text-uppercase text-muted fw-semibold mb-1">
                      {t("BOOKINFO_STATUS_LABEL")}
                    </Form.Label>
                
                  </Form.Group>

                  <Form.Group className="d-flex book-status mb-3" controlId="FormBookStatus">

                    <Form.Select
                      className='book-status-button'
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                      disabled={isSubmitting}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.icon} {option.label}
                        </option>
                      ))}
                    </Form.Select>
                      <Button
                        className='m-2'
                        variant="primary"
                        onClick={handleAddToList}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? t("BOOKINFO_ADDING") : t("BOOKINFO_ADD_BUTTON")}
                      </Button>

                  </Form.Group>
                </Form>

                {feedback && (
                  <Alert
                    variant={feedback.type === "success" ? "success" : "danger"}
                    className="mt-3 mb-0 py-2"
                  >
                    {feedback.message}
                  </Alert>
                )}
              </div>
      
              

          </div>
          
          <div className="col-sm overflow-visible" id="chapter_list_container" style={{ backgroundImage:`url('${backgroundUrl}')`}}>
            <ul className="chapter_list overflow-auto">
              {bookData.chapters.map(chapter => (
                <li key={chapter.chapter_id}>
                  <a
                    className="chapter_link"
                    href={chapter.chapter_hash ? `/chapter?id=${chapter.chapter_hash}` : `/chapter?book_id=${chapter.book_id}&chapter_id=${chapter.chapter_id}`}
                  >
                    <span className="chapter_name">{chapter.chapter_name}</span>
                    <span>{chapter.chapter_resume}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          
      </div>
    </div>

  );
};

export default BookInfo;
